import Vue, { CreateElement } from '@src/vue'
import { Component } from 'vue-property-decorator'
import camelcaseKeys from 'camelcase-keys'

import { Caption, Subtitle1, Subtitle2 } from '@src/elements/typography/typography'
import { OptionGroup } from '@src/elements/select/option-group'
import { Select } from '@src/elements/select/select'
import { AffectedProduct, StationIssueType, SupportTicket } from '@src/data/support-ticket'
import { Option } from '@src/elements/select/option'
import { Input } from '@src/elements/input/input'
import { DatePicker } from '@src/elements/input/date-picker'
import { Button } from '@src/elements/button/button'
import { validateRequiredField, validateSupportedFileType, isFieldFilled, isEmailValid } from '@src/util/validation'
import { SUPPORTED_IMAGE_TYPES, SUPPORTED_VIDEO_TYPES } from '@src/util/constants'
import { ContentBox } from '@src/elements/content-box/content-box'
import { InputFootnote } from '@src/elements/input/input-footnote'
import { Textarea } from '@src/elements/input/textarea'
import { Divider } from '@src/elements/divider/divider'
import ApiClient from '@src/lib/api_client'
import { snakecaseKeys, track } from '@src/util/functions'
import { API } from '@src/lib/api_path'
import Member from '@src/data/member'
import Station from '@src/data/station'
import { SearchMembers } from '@src/routes/support/components/platforms/search-members'

import '../../support.sass'

@Component({
  computed: {
    selectedExercises () {
      return this.$store.state.new_workout.workoutExercises
    },
    locale () {
      return this.$store.state.i18n.locale || 'en-gb'
    }
  }
})
export class PlatformsSupport extends Vue {

  static BROWSERS: string[] = ['Chrome', 'Safari', 'Firefox', 'Edge', 'Opera', 'Mobile Safari', 'Mobile Chrome']

  protected ticket: SupportTicket = new SupportTicket()

  protected errors: string[] = []

  protected apiClient: ApiClient = new ApiClient()

  protected stations: Station[] = []
  private locale: string

  // tslint:disable-next-line:member-ordering
  protected isSubmitting: boolean = false

  constructor () {
    super()
    track('/contact')
  }

  created () {
    this.errors = []

    return this.loadStations()
  }

  updated () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('SUPPORT.SECTION')}`
  }

  setAffectedProduct (value: AffectedProduct | null) {
    this.ticket = new SupportTicket({
      affectedProduct: value,
      memberId: value === AffectedProduct.ANDROID || value === AffectedProduct.IOS ? null : this.$store.state.user.id,
      stationIssueType: value === null ? null : this.ticket.stationIssueType,
      date: value ? this.ticket.date : null,
      exerciseId: value ? this.ticket.exerciseId : null
    })
    if (value && value.toString().includes('station')) {
      this.ticket.stationIssueType = StationIssueType.STATION_HARDWARE
    }
    this.errors = []
  }

  setStationIssueType (value: StationIssueType) {
    // reset the specific fields for each type
    this.ticket = new SupportTicket({
      affectedProduct: this.ticket.affectedProduct,
      stationIssueType: value,
      stationId: this.ticket.stationId,
      memberId: this.$store.state.user.id,
      date: this.ticket.date
    })

    // reset the errors for specific fields (only validate the 2 common fields if these had an error previously)
    let errors: string[] = []
    if (this.errors.includes('stationId') && validateRequiredField(this.ticket.stationId)) {
      errors.push('stationId')
    }
    this.errors = errors
  }

  onFileChange (e) {
    let files = e.target.files || e.dataTransfer.files
    if (!files.length) {
      return
    }

    // validate file format
    if (validateSupportedFileType(files[0].type, SUPPORTED_VIDEO_TYPES) || validateSupportedFileType(files[0].type, SUPPORTED_IMAGE_TYPES)) {
      let reader = new FileReader()
      reader.readAsDataURL(files[0])
      reader.onload = () => {
        // file as base64
        const buffer = reader.result

        this.ticket.attachment = {
          content: buffer,
          filename: files[0].name,
          content_type: files[0].type
        }
      }
    } else {
      this.$store.dispatch('showToast', { message: this.$t('SUPPORT_PAGE.FORMAT_ERROR'), fadeOut: false })
    }
  }

  setSelectedMemberId (member: Member) {
    this.ticket.memberId = member ? member.id : null
  }

  submit () {
    this.isSubmitting = true
    this.validate()

    if (this.errors.length === 0) {
      return this.apiClient.post(API.TRAINERS_API.SUPPORT_TICKETS, snakecaseKeys(this.ticket.formatTicket()))
        .then(() => {
          this.$store.dispatch('showToast', { message: this.$t('SUPPORT_PAGE.TICKET_SUBMITTED'), fadeOut: true })
          this.setAffectedProduct(null)
        })
        .finally(() => {
          this.$nextTick(() => {
            this.isSubmitting = false
          })
        })
    } else {
      this.isSubmitting = false
    }
  }

  render (h: CreateElement) {
    const faqUrl = this.locale.includes('de') ? 'https://support.pixformance.com/portal/kb' : 'https://en.support.pixformance.com/portal/kb'

    return <div class='support-wrapper'>

      <div class='info-wrapper'>
        <Subtitle1 class='intro-paragraph'>{this.$t('SUPPORT_PAGE.CONTACT_SUPPORT_INTRO')}</Subtitle1>
        <Caption class='feedback-paragraph'>{this.$t('SUPPORT_PAGE.CONTACT_SUPPORT_FEEDBACK')}</Caption>
      </div>

      <ContentBox>
        <div slot='content'>
          {this.renderOptions(h)}
          {!this.ticket.affectedProduct ?
            <InputFootnote>{this.$t('SUPPORT_PAGE.FORM.CHOOSE_AFFECTED_PRODUCT_INFO')}</InputFootnote> : null}

          {this.ticket.affectedProduct ?
            [AffectedProduct.STATION_3D, AffectedProduct.STATION_2D].includes(this.ticket.affectedProduct) ? this.renderStationSupportForm(h)
              : [AffectedProduct.PLATFORM, AffectedProduct.PLATFORM_OLD].includes(this.ticket.affectedProduct) ? this.renderWebSupportForm(h)
              : [AffectedProduct.IOS, AffectedProduct.ANDROID].includes(this.ticket.affectedProduct) ? this.renderMobileSupportForm(h) : null
            : null}

          <Divider />
          <div class='support-footer'>
            <Subtitle2>{this.ticket.affectedProduct === AffectedProduct.STATION_3D && this.ticket.stationIssueType === StationIssueType.STATION_HARDWARE ?
              `${this.$t('SUPPORT_PAGE.CALL_SUPPORT')}: +49 5207 92 99 555` : null}</Subtitle2>
            <Subtitle2>{`${this.$t('SUPPORT_PAGE.SUPPORT_AND_FAQ')}: `} <a href={faqUrl} target='_blank'>support.pixformance.com</a></Subtitle2>
          </div>
        </div>

        {[AffectedProduct.PLATFORM, AffectedProduct.PLATFORM_OLD, AffectedProduct.IOS, AffectedProduct.ANDROID].includes(this.ticket.affectedProduct!) || this.ticket.stationIssueType ?
          <div slot='bottom'>
            <Button
              label={this.$t('SUPPORT_PAGE.FORM.SUBMIT')}
              click={this.submit}
              type='elevated'
              ref='submitBtn'
              disabled={this.isSubmitting}
            />
          </div> : null}

      </ContentBox>
    </div>
  }

  renderOptions (h: CreateElement) {
    return <Select ref='supportLevel' value={this.ticket.affectedProduct ? this.$t(`SUPPORT_PAGE.FORM.PRODUCTS.${this.ticket.affectedProduct.toUpperCase()}`) : ''}
                   label={this.$t('SUPPORT_PAGE.FORM.CHOOSE_AFFECTED_PRODUCT')}>
      <OptionGroup>
        <Option type='single-option' value={AffectedProduct.STATION_3D} label={this.$t('SUPPORT_PAGE.FORM.PRODUCTS.STATION_3D')}
                selected={this.ticket.affectedProduct === AffectedProduct.STATION_3D} select={this.setAffectedProduct} ref={AffectedProduct.STATION_3D} />
        <Option type='single-option' value={AffectedProduct.STATION_2D} label={this.$t('SUPPORT_PAGE.FORM.PRODUCTS.STATION_2D')}
                selected={this.ticket.affectedProduct === AffectedProduct.STATION_2D} select={this.setAffectedProduct} ref={AffectedProduct.STATION_2D} />
        <Option type='single-option' value={AffectedProduct.PLATFORM} label={this.$t('SUPPORT_PAGE.FORM.PRODUCTS.PLATFORM')}
                selected={this.ticket.affectedProduct === AffectedProduct.PLATFORM} select={this.setAffectedProduct} ref={AffectedProduct.PLATFORM} />
        <Option type='single-option' value={AffectedProduct.PLATFORM_OLD} label={this.$t('SUPPORT_PAGE.FORM.PRODUCTS.PLATFORM_OLD')}
                selected={this.ticket.affectedProduct === AffectedProduct.PLATFORM_OLD} select={this.setAffectedProduct} ref={AffectedProduct.PLATFORM_OLD} />
        <Option type='single-option' value={AffectedProduct.IOS} label={this.$t('SUPPORT_PAGE.FORM.PRODUCTS.IOS')}
                selected={this.ticket.affectedProduct === AffectedProduct.IOS} select={this.setAffectedProduct} ref={AffectedProduct.IOS} />
        <Option type='single-option' value={AffectedProduct.ANDROID} label={this.$t('SUPPORT_PAGE.FORM.PRODUCTS.ANDROID')}
                selected={this.ticket.affectedProduct === AffectedProduct.ANDROID} select={this.setAffectedProduct} ref={AffectedProduct.ANDROID} />
      </OptionGroup>
    </Select>
  }

  renderStationSupportForm (h: CreateElement) {

    const stations = this.stations
      .map((station: Station) => {
        if (this.ticket.affectedProduct!.toString().includes(station.stationType)) {
          return <Option type='single-option' value={`${station.id}`} label={[station.name, station.displayName].filter(text => text).join(' - ')}
                         selected={this.ticket.stationId === station.id} select={value => this.ticket.stationId = Number(value)} />
        }
      })
    const selectedStation = this.ticket.stationId ? this.stations.filter(station => station.id === this.ticket.stationId)[0] : null
    const stationName = selectedStation ? [selectedStation.name, selectedStation.displayName].filter(text => text).join(' - ') : ''

    return <div ref='ticketForm'>
      <Select ref='stationId' value={stationName} label={`${this.$t('SUPPORT_PAGE.SELECT_STATION')}*`} class={this.errors.includes('stationId') ? 'error' : ''}>
        <OptionGroup>
          {stations}
        </OptionGroup>
      </Select>
      <InputFootnote>{this.$t('SUPPORT_PAGE.STATION.STATION_HARDWARE_VERSION')}</InputFootnote>

      <Select ref='issueType' value={this.ticket.stationIssueType ? this.$t(`SUPPORT_PAGE.STATION.STATION_ISSUE.${this.ticket.stationIssueType!.toUpperCase()}`) : ''}
              class={this.errors.includes('stationIssueType') ? 'error' : ''} label={`${this.$t('SUPPORT_PAGE.FORM.PROBLEM_TYPE')}*`}>
        <OptionGroup>
          <Option type='single-option' value={StationIssueType.STATION_HARDWARE} label={this.$t('SUPPORT_PAGE.STATION.STATION_ISSUE.STATION_HARDWARE')}
                  selected={this.ticket.stationIssueType === StationIssueType.STATION_HARDWARE} select={this.setStationIssueType} ref={StationIssueType.STATION_HARDWARE} />
        </OptionGroup>
      </Select>

        <div class='support-form'>
          <DatePicker value={this.ticket.date} change={value => this.ticket.date = value} class={this.errors.includes('date') ? 'error' : ''} label={`${this.$t('DATE')}*`}
                      allowFutureDate={false} />
          <InputFootnote>{this.$t('SUPPORT_PAGE.FORM.DATE_PICKER')}</InputFootnote>
          <Textarea value={this.ticket.description} keyup={value => this.ticket.description = value} class={this.errors.includes('description') ? 'error' : ''}
                    label={`${this.$t('SUPPORT_PAGE.STATION.FUNCTIONALITY_HARDWARE.1')}*`} />
          <Textarea value={this.ticket.affectedStations} keyup={value => this.ticket.affectedStations = value}
                    class={this.errors.includes('affectedStations') ? 'error' : ''} label={`${this.$t('SUPPORT_PAGE.STATION.FUNCTIONALITY_HARDWARE.2')}*`} />
          <Textarea value={this.ticket.attemptsToFix} keyup={value => this.ticket.attemptsToFix = value}
                    class={this.errors.includes('attemptsToFix') ? 'error' : ''} label={`${this.$t('SUPPORT_PAGE.STATION.FUNCTIONALITY_HARDWARE.3')}*`} />
          {
            // @ts-ignore
          }<Input value={this.ticket.email} valueChanged={(value: string) => this.ticket.email = value}
                 class={this.errors.includes('email') ? 'error' : ''} label={`${this.$t('SUPPORT.EMAIL')}`} />
        </div>

      {this.renderUploadForm(h)}
    </div>
  }

  renderWebSupportForm (h: CreateElement) {
    const browsers = PlatformsSupport.BROWSERS.map(browser => {
      return <Option type='single-option' value={browser} label={browser}
                     selected={this.ticket.browser === browser} select={value => this.ticket.browser = value} />
    })

    return <div ref='ticketForm' class='support-form'>
      <DatePicker value={this.ticket.date} change={value => this.ticket.date = value} class={this.errors.includes('date') ? 'error' : ''} label={`${this.$t('DATE')}*`}
                  allowFutureDate={false} />
      <InputFootnote>{this.$t('SUPPORT_PAGE.FORM.DATE_PICKER')}</InputFootnote>
      <Select ref='memberId' value={this.ticket.browser} label={`${this.$t('SUPPORT_PAGE.WEB_PLATFORM.1')}*`} class={this.errors.includes('browser') ? 'error' : ''}>
        <OptionGroup>
          {browsers}
        </OptionGroup>
      </Select>
      <Textarea value={this.ticket.description} keyup={value => this.ticket.description = value} class={this.errors.includes('description') ? 'error' : ''}
                label={`${this.$t('SUPPORT_PAGE.WEB_PLATFORM.3')}*`} />
      <Textarea value={this.ticket.howToReproduce} keyup={value => this.ticket.howToReproduce = value} label={`${this.$t('SUPPORT_PAGE.WEB_PLATFORM.4')}*`}
                class={this.errors.includes('howToReproduce') ? 'error' : ''} />
      <Textarea value={this.ticket.reproducibleInAnotherBrowser} keyup={value => this.ticket.reproducibleInAnotherBrowser = value}
                class={this.errors.includes('reproducibleInAnotherBrowser') ? 'error' : ''} label={`${this.$t('SUPPORT_PAGE.WEB_PLATFORM.5')}*`} />
      {
        // @ts-ignore
      }<Input value={this.ticket.email} valueChanged={(value: string) => this.ticket.email = value}
             class={this.errors.includes('email') ? 'error' : ''} label={`${this.$t('SUPPORT.EMAIL')}`} />
      {this.renderUploadForm(h)}
    </div>
  }

  renderMobileSupportForm (h: CreateElement) {
    return <div ref='ticketForm' class='support-form'>
      <DatePicker value={this.ticket.date} change={value => this.ticket.date = value} class={this.errors.includes('date') ? 'error' : ''} label={`${this.$t('DATE')}*`}
                  allowFutureDate={false} />
      <InputFootnote>{this.$t('SUPPORT_PAGE.FORM.DATE_PICKER')}</InputFootnote>
      <Input value={this.ticket.device} valueChanged={value => this.ticket.device = value} class={this.errors.includes('device') ? 'error' : ''}
             label={`${this.$t('SUPPORT_PAGE.MOBILE.1')}*`} />
      <Input value={this.ticket.appVersion} valueChanged={value => this.ticket.appVersion = value} class={this.errors.includes('appVersion') ? 'error' : ''}
             label={`${this.$t('SUPPORT_PAGE.MOBILE.2')}*`} />
      <div class={this.errors.includes('memberId') ? 'search-members error' : 'search-members'}>
        <SearchMembers hideDate={true} callback={this.setSelectedMemberId} />
        <InputFootnote>{this.$t('SUPPORT_PAGE.MOBILE.3')}</InputFootnote>
      </div>
      <Textarea value={this.ticket.description} keyup={value => this.ticket.description = value} class={this.errors.includes('description') ? 'error' : ''}
                label={`${this.$t('SUPPORT_PAGE.MOBILE.4')}*`} />
      {
        // @ts-ignore
      }<Input value={this.ticket.email} valueChanged={(value: string) => this.ticket.email = value}
             class={this.errors.includes('email') ? 'error' : ''} label={`${this.$t('SUPPORT.EMAIL')}`} />
      {this.renderUploadForm(h)}
    </div>
  }

  renderUploadForm (h: CreateElement) {
    return <div>
      <Subtitle1 class={'upload-notice'}>{this.$t('SUPPORT_PAGE.FORM.UPLOAD_MEDIA')}</Subtitle1>
      {this.ticket.attachment ? this.renderPreview(h, this.ticket.attachment.filename, this.ticket.attachment.content_type, this.ticket.attachment.content) : null}
      <div class='upload-form'>
        <Button label={this.$t('UPLOAD')} color='grey' class='upload-btn' type='elevated' />
        <Input ref='file-upload' class='file-upload' htmlId='file-upload' type='file' fileChanged={this.onFileChange} />
      </div>
    </div>
  }

  renderPreview (h: CreateElement, fileName: any, type: string, source: string) {
    return <div class='preview'>
      {type.includes('video') ?
        <video muted>
          <source src={source} />
        </video>
        : <img src={source} />}
    </div>
  }

  protected loadStations () {
    return this.apiClient.get<any>(API.TRAINERS_API.STATIONS)
      .then(response => {
        this.stations = response.stations.map((attrs) => new Station(camelcaseKeys(attrs)))
      })
      .catch(error => console.error('failed to retrieve stations', error))
  }

  protected validate () {
    if (this.ticket.affectedProduct === AffectedProduct.STATION_2D || this.ticket.affectedProduct === AffectedProduct.STATION_3D) {
      this.validateStationSupportForm()
    } else if (this.ticket.affectedProduct === AffectedProduct.PLATFORM || this.ticket.affectedProduct === AffectedProduct.PLATFORM_OLD) {
      this.validateWebSupportForm()
    } else if (this.ticket.affectedProduct === AffectedProduct.IOS || this.ticket.affectedProduct === AffectedProduct.ANDROID) {
      this.validateMobileSupportForm()
    }
  }

  private validateStationSupportForm () {
    let errors: string[] = []
    if (isFieldFilled(this.ticket.email) && !isEmailValid(this.ticket.email)) {
      errors.push('email')
    }
    if (validateRequiredField(this.ticket.stationId)) {
      errors.push('stationId')
    }
    if (validateRequiredField(this.ticket.date)) {
      errors.push('date')
    }
    if (validateRequiredField(this.ticket.description)) {
      errors.push('description')
    }

    if (!this.ticket.stationIssueType) {
      errors.push('stationIssueType')
    } else if (this.ticket.stationIssueType === StationIssueType.STATION_SOFTWARE) {
      if (validateRequiredField(this.ticket.exerciseId)) {
        errors.push('exerciseId')
      }
    } else {
      if (validateRequiredField(this.ticket.affectedStations)) {
        errors.push('affectedStations')
      }
      if (validateRequiredField(this.ticket.attemptsToFix)) {
        errors.push('attemptsToFix')
      }
    }

    this.errors = errors
  }

  private validateWebSupportForm () {
    let errors: string[] = []
    if (isFieldFilled(this.ticket.email) && !isEmailValid(this.ticket.email)) {
      errors.push('email')
    }
    if (validateRequiredField(this.ticket.date)) {
      errors.push('date')
    }
    if (validateRequiredField(this.ticket.description)) {
      errors.push('description')
    }
    if (validateRequiredField(this.ticket.browser)) {
      errors.push('browser')
    }
    if (validateRequiredField(this.ticket.howToReproduce)) {
      errors.push('howToReproduce')
    }
    if (validateRequiredField(this.ticket.reproducibleInAnotherBrowser)) {
      errors.push('reproducibleInAnotherBrowser')
    }

    this.errors = errors
  }

  private validateMobileSupportForm () {
    let errors: string[] = []
    if (isFieldFilled(this.ticket.email) && !isEmailValid(this.ticket.email)) {
      errors.push('email')
    }
    if (validateRequiredField(this.ticket.date)) {
      errors.push('date')
    }
    if (validateRequiredField(this.ticket.description)) {
      errors.push('description')
    }
    if (validateRequiredField(this.ticket.device)) {
      errors.push('device')
    }
    if (validateRequiredField(this.ticket.appVersion)) {
      errors.push('appVersion')
    }
    if (validateRequiredField(this.ticket.memberId)) {
      errors.push('memberId')
    }

    this.errors = errors
  }
}

@Component
export class MembersSupport extends PlatformsSupport {

  updated () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('SUPPORT.SECTION')}`
  }

  setAffectedProduct (value: AffectedProduct | null) {
    this.ticket = new SupportTicket({
      affectedProduct: value,
      memberId: this.$store.state.user.id,
      stationIssueType: value === null ? null : this.ticket.stationIssueType,
      date: this.ticket.date
    })
    this.errors = []
  }

  renderOptions (h: CreateElement) {
    return <Select ref='supportLevel' value={this.ticket.affectedProduct ? this.$t(`SUPPORT_PAGE.FORM.PRODUCTS.${this.ticket.affectedProduct.toUpperCase()}`) : ''}
                   label={this.$t('SUPPORT_PAGE.FORM.CHOOSE_AFFECTED_PRODUCT')}>
      <OptionGroup>
        <Option type='single-option' value={AffectedProduct.PLATFORM} label={this.$t('SUPPORT_PAGE.FORM.PRODUCTS.PLATFORM')}
                selected={this.ticket.affectedProduct === AffectedProduct.PLATFORM} select={this.setAffectedProduct} ref={AffectedProduct.PLATFORM} />
        <Option type='single-option' value={AffectedProduct.PLATFORM_OLD} label={this.$t('SUPPORT_PAGE.FORM.PRODUCTS.PLATFORM_OLD')}
                selected={this.ticket.affectedProduct === AffectedProduct.PLATFORM_OLD} select={this.setAffectedProduct} ref={AffectedProduct.PLATFORM_OLD} />
        <Option type='single-option' value={AffectedProduct.IOS} label={this.$t('SUPPORT_PAGE.FORM.PRODUCTS.IOS')}
                selected={this.ticket.affectedProduct === AffectedProduct.IOS} select={this.setAffectedProduct} ref={AffectedProduct.IOS} />
        <Option type='single-option' value={AffectedProduct.ANDROID} label={this.$t('SUPPORT_PAGE.FORM.PRODUCTS.ANDROID')}
                selected={this.ticket.affectedProduct === AffectedProduct.ANDROID} select={this.setAffectedProduct} ref={AffectedProduct.ANDROID} />
      </OptionGroup>
    </Select>
  }

  renderMobileSupportForm (h: CreateElement) {
    return <div ref='ticketForm' class='support-form'>
      <DatePicker value={this.ticket.date} change={value => this.ticket.date = value} class={this.errors.includes('date') ? 'error' : ''} label={`${this.$t('DATE')}*`}
                  allowFutureDate={false} />
      <InputFootnote>{this.$t('SUPPORT_PAGE.FORM.DATE_PICKER')}</InputFootnote>
      <Input value={this.ticket.device} valueChanged={value => this.ticket.device = value} class={this.errors.includes('device') ? 'error' : ''}
             label={`${this.$t('SUPPORT_PAGE.MOBILE.1')}*`} />
      <Input value={this.ticket.appVersion} valueChanged={value => this.ticket.appVersion = value} class={this.errors.includes('appVersion') ? 'error' : ''}
             label={`${this.$t('SUPPORT_PAGE.MOBILE.2')}*`} />
      <Textarea value={this.ticket.description} keyup={value => this.ticket.description = value} class={this.errors.includes('description') ? 'error' : ''}
                label={`${this.$t('SUPPORT_PAGE.MOBILE.4')}*`} />
      {
        // @ts-ignore
      }<Input value={this.ticket.email} valueChanged={(value: string) => this.ticket.email = value}
             class={this.errors.includes('email') ? 'error' : ''} label={`${this.$t('SUPPORT.EMAIL')}`} />
      {this.renderUploadForm(h)}
    </div>
  }

  submit () {
    this.isSubmitting = true
    this.validate()

    if (this.errors.length === 0) {
      return this.apiClient.post(API.MEMBERS_API.SUPPORT_TICKETS, snakecaseKeys(this.ticket.formatTicket()))
        .then(() => {
          this.$store.dispatch('showToast', { message: this.$t('SUPPORT_PAGE.TICKET_SUBMITTED'), fadeOut: true })
          this.setAffectedProduct(null)
        })
        .finally(() => {
          this.$nextTick(() => {
            this.isSubmitting = false
          })
        })
    } else {
      this.isSubmitting = false
    }
  }

  protected loadStations () {
    return this.apiClient.get<any>(API.MEMBERS_API.STATIONS)
      .then(response => {
        this.stations = response.stations.map((attrs) => new Station(camelcaseKeys(attrs)))
      })
      .catch(error => console.error('failed to retrieve stations', error))
  }
}
